import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";
import { ReviewStatus, ReviewStatusSchema } from "@/schemas/review-status";

import {
	KnowledgeDbIcpApi,
	KnowledgeDbIcpResponseApi,
	KnowledgeDbIcpResponseSchema,
	KnowledgeDbIcpSchema,
	KnowledgeDbProductApi,
	KnowledgeDbProductResponseApi,
	KnowledgeDbProductResponseSchema,
	KnowledgeDbProductSchema,
	KnowledgeDbCaseStudyApi,
	KnowledgeDbCaseStudyResponseApi,
	KnowledgeDbCaseStudyResponseSchema,
	KnowledgeDbCaseStudySchema,
	KnowledgeDbObjectionApi,
	KnowledgeDbObjectionResponseApi,
	KnowledgeDbObjectionResponseSchema,
	KnowledgeDbObjectionSchema,
	KnowledgeDbObjectionCategoryApi,
	KnowledgeDbObjectionCategoryResponseApi,
	KnowledgeDbObjectionCategoryResponseSchema,
	KnowledgeDbObjectionCategorySchema,
	KnowledgeDbSettingApi,
	KnowledgeDbSettingSchema,
	KnowledgeDbDeleteResponse,
	KnowledgeDbDeleteResponseSchema,
	KnowledgeDbCaseStudyAutoUpdateResponse,
	KnowledgeDbCaseStudyAutoUpdateResponseSchema,
	KnowledgeDbBulkApproveResponse,
	KnowledgeDbBulkApproveResponseSchema,
	KnowledgeDbBulkApproveProductsResponse,
	KnowledgeDbBulkApproveProductsResponseSchema,
	KnowledgeDbBulkApproveCaseStudiesResponse,
	KnowledgeDbBulkApproveCaseStudiesResponseSchema,
	KnowledgeDbBulkApproveObjectionsResponse,
	KnowledgeDbBulkApproveObjectionsResponseSchema,
	ApplySegmentChangesResponse,
	ApplySegmentChangesResponseSchema,
	PendingSegmentSync,
	PendingSegmentSyncListSchema,
	DeletePendingSegmentSyncResponse,
	DeletePendingSegmentSyncResponseSchema,
	SegmentChangeItem,
} from "../../pages/KnowledgeDb/schema/knowledgeDbApi";

export const knowledgeDbApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// ── Review status (left-nav orange dot) ──
		getKnowledgeDbReviewStatus: builder.query<ReviewStatus, void>({
			query: () => ({
				url: "knowledge-db/review-status",
				responseSchema: ReviewStatusSchema,
			}),
			providesTags: [
				"KnowledgeDbReviewStatus",
				"KnowledgeDbIcp",
				"KnowledgeDbProduct",
				"KnowledgeDbCaseStudy",
				"KnowledgeDbObjection",
				"KnowledgeDbPendingSync",
			],
		}),
		// ── ICPs ──
		getIcps: builder.query<KnowledgeDbIcpResponseApi, RequestPaginationParams>({
			query: (params) => ({
				url: "knowledge-db/icps",
				params: { ...(params && { ...params }) },
				responseSchema: KnowledgeDbIcpResponseSchema,
			}),
			providesTags: ["KnowledgeDbIcp"],
		}),
		getIcp: builder.query<KnowledgeDbIcpApi, number>({
			query: (id) => ({
				url: `knowledge-db/icps/${id}`,
				responseSchema: KnowledgeDbIcpSchema,
			}),
			providesTags: ["KnowledgeDbIcp"],
		}),
		createIcp: builder.mutation<KnowledgeDbIcpApi, Record<string, unknown>>({
			query: (body) => ({
				url: "knowledge-db/icps",
				method: "POST",
				body,
				responseSchema: KnowledgeDbIcpSchema,
			}),
			invalidatesTags: ["KnowledgeDbIcp"],
		}),
		updateIcp: builder.mutation<KnowledgeDbIcpApi, { id: number; body: Record<string, unknown> }>({
			query: ({ id, body }) => ({
				url: `knowledge-db/icps/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbIcpSchema,
			}),
			invalidatesTags: ["KnowledgeDbIcp"],
		}),
		deleteIcp: builder.mutation<KnowledgeDbDeleteResponse, number>({
			query: (id) => ({
				url: `knowledge-db/icps/${id}`,
				method: "DELETE",
				responseSchema: KnowledgeDbDeleteResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbIcp"],
		}),
		bulkApproveIcps: builder.mutation<KnowledgeDbBulkApproveResponse, { ids: number[] }>({
			query: (body) => ({
				url: "knowledge-db/icps/bulk-approve",
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbBulkApproveResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbIcp"],
		}),

		// ── Products ──
		getKnowledgeDbProducts: builder.query<KnowledgeDbProductResponseApi, RequestPaginationParams>({
			query: (params) => ({
				url: "knowledge-db/products",
				params: { ...(params && { ...params }) },
				responseSchema: KnowledgeDbProductResponseSchema,
			}),
			providesTags: ["KnowledgeDbProduct"],
		}),
		getKnowledgeDbProduct: builder.query<KnowledgeDbProductApi, number>({
			query: (id) => ({
				url: `knowledge-db/products/${id}`,
				responseSchema: KnowledgeDbProductSchema,
			}),
			providesTags: ["KnowledgeDbProduct"],
		}),
		createKnowledgeDbProduct: builder.mutation<KnowledgeDbProductApi, Record<string, unknown>>({
			query: (body) => ({
				url: "knowledge-db/products",
				method: "POST",
				body,
				responseSchema: KnowledgeDbProductSchema,
			}),
			invalidatesTags: ["KnowledgeDbProduct"],
		}),
		updateKnowledgeDbProduct: builder.mutation<
			KnowledgeDbProductApi,
			{ id: number; body: Record<string, unknown> }
		>({
			query: ({ id, body }) => ({
				url: `knowledge-db/products/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbProductSchema,
			}),
			invalidatesTags: ["KnowledgeDbProduct"],
		}),
		deleteKnowledgeDbProduct: builder.mutation<KnowledgeDbDeleteResponse, number>({
			query: (id) => ({
				url: `knowledge-db/products/${id}`,
				method: "DELETE",
				responseSchema: KnowledgeDbDeleteResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbProduct"],
		}),
		bulkApproveProducts: builder.mutation<
			KnowledgeDbBulkApproveProductsResponse,
			{ ids: number[] }
		>({
			query: (body) => ({
				url: "knowledge-db/products/bulk-approve",
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbBulkApproveProductsResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbProduct"],
		}),

		// ── Case Studies ──
		getCaseStudies: builder.query<KnowledgeDbCaseStudyResponseApi, RequestPaginationParams>({
			query: (params) => ({
				url: "knowledge-db/case-studies",
				params: { ...(params && { ...params }) },
				responseSchema: KnowledgeDbCaseStudyResponseSchema,
			}),
			providesTags: ["KnowledgeDbCaseStudy"],
		}),
		getCaseStudy: builder.query<KnowledgeDbCaseStudyApi, number>({
			query: (id) => ({
				url: `knowledge-db/case-studies/${id}`,
				responseSchema: KnowledgeDbCaseStudySchema,
			}),
			providesTags: ["KnowledgeDbCaseStudy"],
		}),
		createCaseStudy: builder.mutation<KnowledgeDbCaseStudyApi, Record<string, unknown>>({
			query: (body) => ({
				url: "knowledge-db/case-studies",
				method: "POST",
				body,
				responseSchema: KnowledgeDbCaseStudySchema,
			}),
			invalidatesTags: ["KnowledgeDbCaseStudy"],
		}),
		updateCaseStudy: builder.mutation<
			KnowledgeDbCaseStudyApi,
			{ id: number; body: Record<string, unknown> }
		>({
			query: ({ id, body }) => ({
				url: `knowledge-db/case-studies/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbCaseStudySchema,
			}),
			invalidatesTags: ["KnowledgeDbCaseStudy"],
		}),
		deleteCaseStudy: builder.mutation<KnowledgeDbDeleteResponse, number>({
			query: (id) => ({
				url: `knowledge-db/case-studies/${id}`,
				method: "DELETE",
				responseSchema: KnowledgeDbDeleteResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbCaseStudy"],
		}),
		bulkApproveCaseStudies: builder.mutation<
			KnowledgeDbBulkApproveCaseStudiesResponse,
			{ ids: number[] }
		>({
			query: (body) => ({
				url: "knowledge-db/case-studies/bulk-approve",
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbBulkApproveCaseStudiesResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbCaseStudy"],
		}),

		// ── Objections ──
		getObjections: builder.query<KnowledgeDbObjectionResponseApi, RequestPaginationParams>({
			query: (params) => ({
				url: "knowledge-db/objections",
				params: { ...(params && { ...params }) },
				responseSchema: KnowledgeDbObjectionResponseSchema,
			}),
			providesTags: ["KnowledgeDbObjection"],
		}),
		getObjection: builder.query<KnowledgeDbObjectionApi, number>({
			query: (id) => ({
				url: `knowledge-db/objections/${id}`,
				responseSchema: KnowledgeDbObjectionSchema,
			}),
			providesTags: ["KnowledgeDbObjection"],
		}),
		createObjection: builder.mutation<KnowledgeDbObjectionApi, Record<string, unknown>>({
			query: (body) => ({
				url: "knowledge-db/objections",
				method: "POST",
				body,
				responseSchema: KnowledgeDbObjectionSchema,
			}),
			invalidatesTags: ["KnowledgeDbObjection"],
		}),
		updateObjection: builder.mutation<
			KnowledgeDbObjectionApi,
			{ id: number; body: Record<string, unknown> }
		>({
			query: ({ id, body }) => ({
				url: `knowledge-db/objections/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbObjectionSchema,
			}),
			invalidatesTags: ["KnowledgeDbObjection"],
		}),
		deleteObjection: builder.mutation<KnowledgeDbDeleteResponse, number>({
			query: (id) => ({
				url: `knowledge-db/objections/${id}`,
				method: "DELETE",
				responseSchema: KnowledgeDbDeleteResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbObjection"],
		}),
		bulkApproveObjections: builder.mutation<
			KnowledgeDbBulkApproveObjectionsResponse,
			{ ids: number[] }
		>({
			query: (body) => ({
				url: "knowledge-db/objections/bulk-approve",
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbBulkApproveObjectionsResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbObjection"],
		}),

		// ── Objection Categories ──
		getObjectionCategories: builder.query<
			KnowledgeDbObjectionCategoryResponseApi,
			RequestPaginationParams
		>({
			query: (params) => ({
				url: "knowledge-db/objection-categories",
				params: { ...(params && { ...params }) },
				responseSchema: KnowledgeDbObjectionCategoryResponseSchema,
			}),
			providesTags: ["KnowledgeDbObjectionCategory"],
		}),
		getObjectionCategory: builder.query<KnowledgeDbObjectionCategoryApi, number>({
			query: (id) => ({
				url: `knowledge-db/objection-categories/${id}`,
				responseSchema: KnowledgeDbObjectionCategorySchema,
			}),
			providesTags: ["KnowledgeDbObjectionCategory"],
		}),
		createObjectionCategory: builder.mutation<
			KnowledgeDbObjectionCategoryApi,
			{ name: string; isCustom?: boolean }
		>({
			query: (body) => ({
				url: "knowledge-db/objection-categories",
				method: "POST",
				body,
				responseSchema: KnowledgeDbObjectionCategorySchema,
			}),
			invalidatesTags: ["KnowledgeDbObjectionCategory"],
		}),
		updateObjectionCategory: builder.mutation<
			KnowledgeDbObjectionCategoryApi,
			{ id: number; body: { name?: string; isCustom?: boolean } }
		>({
			query: ({ id, body }) => ({
				url: `knowledge-db/objection-categories/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbObjectionCategorySchema,
			}),
			invalidatesTags: ["KnowledgeDbObjectionCategory"],
		}),
		deleteObjectionCategory: builder.mutation<KnowledgeDbDeleteResponse, number>({
			query: (id) => ({
				url: `knowledge-db/objection-categories/${id}`,
				method: "DELETE",
				responseSchema: KnowledgeDbDeleteResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbObjectionCategory", "KnowledgeDbObjection"],
		}),

		// ── Settings ──
		getSettings: builder.query<KnowledgeDbSettingApi, void>({
			query: () => ({
				url: "knowledge-db/settings",
				responseSchema: KnowledgeDbSettingSchema,
			}),
			providesTags: ["KnowledgeDbSetting"],
		}),
		upsertSettings: builder.mutation<
			KnowledgeDbSettingApi,
			{ autoUpdateEnabled?: boolean; caseStudyUrl?: string; caseStudyDocumentUrl?: string }
		>({
			query: (body) => ({
				url: "knowledge-db/settings",
				method: "POST",
				body,
				responseSchema: KnowledgeDbSettingSchema,
			}),
			invalidatesTags: ["KnowledgeDbSetting"],
		}),
		updateSettings: builder.mutation<
			KnowledgeDbSettingApi,
			{
				id: number;
				body: {
					autoUpdateEnabled?: boolean;
					caseStudyUrl?: string;
					caseStudyDocumentUrl?: string;
				};
			}
		>({
			query: ({ id, body }) => ({
				url: `knowledge-db/settings/${id}`,
				method: "PATCH",
				body,
				responseSchema: KnowledgeDbSettingSchema,
			}),
			invalidatesTags: ["KnowledgeDbSetting"],
		}),

		// ── Case Study Auto-Update ──
		triggerCaseStudyAutoUpdate: builder.mutation<KnowledgeDbCaseStudyAutoUpdateResponse, void>({
			query: () => ({
				url: "knowledge-db/case-study-auto-update",
				method: "POST",
				responseSchema: KnowledgeDbCaseStudyAutoUpdateResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbCaseStudy"],
		}),

		// ── Segment Sync ──
		applySegmentChanges: builder.mutation<
			ApplySegmentChangesResponse,
			{
				action: "GENERATE_NOW" | "REMIND_LATER" | "SKIP";
				changes?: SegmentChangeItem[];
				pendingSyncId?: number | null;
			}
		>({
			query: (body) => ({
				url: "knowledge-db/apply-segment-changes",
				method: "POST",
				body,
				responseSchema: ApplySegmentChangesResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbIcp", "KnowledgeDbPendingSync"],
		}),
		getPendingSegmentSync: builder.query<PendingSegmentSync[], void>({
			query: () => ({
				url: "knowledge-db/pending-segment-sync",
				responseSchema: PendingSegmentSyncListSchema,
			}),
			providesTags: ["KnowledgeDbPendingSync"],
		}),
		deletePendingSegmentSync: builder.mutation<DeletePendingSegmentSyncResponse, number>({
			query: (id) => ({
				url: `knowledge-db/pending-segment-sync/${id}`,
				method: "DELETE",
				responseSchema: DeletePendingSegmentSyncResponseSchema,
			}),
			invalidatesTags: ["KnowledgeDbPendingSync"],
		}),
	}),
});

export const {
	// Review status
	useGetKnowledgeDbReviewStatusQuery,
	// ICPs
	useGetIcpsQuery,
	useGetIcpQuery,
	useCreateIcpMutation,
	useUpdateIcpMutation,
	useDeleteIcpMutation,
	useBulkApproveIcpsMutation,
	// Products
	useGetKnowledgeDbProductsQuery,
	useGetKnowledgeDbProductQuery,
	useCreateKnowledgeDbProductMutation,
	useUpdateKnowledgeDbProductMutation,
	useDeleteKnowledgeDbProductMutation,
	useBulkApproveProductsMutation,
	// Case Studies
	useGetCaseStudiesQuery,
	useGetCaseStudyQuery,
	useCreateCaseStudyMutation,
	useUpdateCaseStudyMutation,
	useDeleteCaseStudyMutation,
	useBulkApproveCaseStudiesMutation,
	// Objections
	useGetObjectionsQuery,
	useGetObjectionQuery,
	useCreateObjectionMutation,
	useUpdateObjectionMutation,
	useDeleteObjectionMutation,
	useBulkApproveObjectionsMutation,
	// Objection Categories
	useGetObjectionCategoriesQuery,
	useGetObjectionCategoryQuery,
	useCreateObjectionCategoryMutation,
	useUpdateObjectionCategoryMutation,
	useDeleteObjectionCategoryMutation,
	// Settings
	useGetSettingsQuery,
	useUpsertSettingsMutation,
	useUpdateSettingsMutation,
	// Case Study Auto-Update
	useTriggerCaseStudyAutoUpdateMutation,
	// Segment Sync
	useApplySegmentChangesMutation,
	useGetPendingSegmentSyncQuery,
	useDeletePendingSegmentSyncMutation,
} = knowledgeDbApi;
